import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Play, RotateCcw, Trophy, Ticket, ShieldCheck } from 'lucide-react';
import sorteoEnVivoService from '../../services/sorteoEnVivo.service';
import PageHeader from '../../components/shared/PageHeader';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '../../components/ui/card';
import LoadingBox from '../../components/LoadingBox';
import { getError } from '../../utils';
import DrawStage from './live/DrawStage';
import {
  NAME_MODES,
  formatWinnerName,
  hasWinner,
  nextPendingPremio,
  sortForDraw,
} from './live/liveDrawUtils';

const SELECT_CLASS =
  'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50';

function readPref(key, allowed, fallback) {
  try {
    const v = localStorage.getItem(key);
    return allowed.includes(v) ? v : fallback;
  } catch {
    return fallback;
  }
}
function writePref(key, value) {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* ignore */
  }
}

/**
 * Admin: run a raffle draw live, with a presentation mode made for screen recording.
 * Route: /admin/sorteo-en-vivo?rifa=<id>&sorteo=<id>
 */
export default function SorteoEnVivo() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const rifaId = searchParams.get('rifa') || '';
  const sorteoId = searchParams.get('sorteo') || '';

  const [rifas, setRifas] = useState([]);
  const [loadingRifas, setLoadingRifas] = useState(true);
  const [rifasError, setRifasError] = useState('');

  const [drawState, setDrawState] = useState(null);
  const [loadingState, setLoadingState] = useState(false);
  const [stateError, setStateError] = useState('');

  const [nameMode, setNameModeState] = useState(() =>
    readPref('liveDraw.nameMode', NAME_MODES, 'short')
  );
  const [frame, setFrameState] = useState(() =>
    readPref('liveDraw.frame', ['fill', 'vertical', 'horizontal'], 'fill')
  );
  const [stagePremioId, setStagePremioId] = useState(null);
  const [stageOpen, setStageOpen] = useState(false);
  const [resettingId, setResettingId] = useState(null);

  const setNameMode = useCallback(v => {
    setNameModeState(v);
    writePref('liveDraw.nameMode', v);
  }, []);
  const setFrame = useCallback(v => {
    setFrameState(v);
    writePref('liveDraw.frame', v);
  }, []);

  // ── Load raffles ──
  const loadRifas = useCallback(async () => {
    try {
      setRifasError('');
      const data = await sorteoEnVivoService.getRifas();
      setRifas(Array.isArray(data) ? data : []);
    } catch (err) {
      setRifasError(getError(err));
    } finally {
      setLoadingRifas(false);
    }
  }, []);

  useEffect(() => {
    loadRifas();
  }, [loadRifas]);

  const selectedRifa = useMemo(() => rifas.find(r => r._id === rifaId) || null, [rifas, rifaId]);

  // Auto-select the only sorteo of a raffle
  useEffect(() => {
    if (selectedRifa && !sorteoId && selectedRifa.sorteos.length === 1) {
      setSearchParams(
        { rifa: selectedRifa._id, sorteo: selectedRifa.sorteos[0]._id },
        { replace: true }
      );
    }
  }, [selectedRifa, sorteoId, setSearchParams]);

  // ── Load draw state ──
  const loadState = useCallback(async () => {
    if (!sorteoId) {
      setDrawState(null);
      return;
    }
    setLoadingState(true);
    setStateError('');
    try {
      setDrawState(await sorteoEnVivoService.getDrawState(sorteoId));
    } catch (err) {
      setDrawState(null);
      setStateError(getError(err));
    } finally {
      setLoadingState(false);
    }
  }, [sorteoId]);

  useEffect(() => {
    loadState();
  }, [loadState]);

  const premios = useMemo(() => sortForDraw(drawState?.premios), [drawState]);
  const drawnCount = premios.filter(hasWinner).length;

  // ── Actions ──
  const handleDraw = useCallback(
    async premio => {
      const res = await sorteoEnVivoService.drawPremio(premio._id);
      const nro = res?.premio?.nroGanador;
      setDrawState(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          premios: prev.premios.map(p => (p._id === res.premio._id ? { ...p, ...res.premio } : p)),
          eligibleNumbers: prev.eligibleNumbers.filter(n => n !== nro),
          eligibleCount: Math.max(0, (prev.eligibleCount || 0) - 1),
        };
      });
      setRifas(prev =>
        prev.map(r => ({
          ...r,
          sorteos: r.sorteos.map(s =>
            s._id === sorteoId ? { ...s, premiosSorteados: (s.premiosSorteados || 0) + 1 } : s
          ),
        }))
      );
      return res;
    },
    [sorteoId]
  );

  const handleReset = async premio => {
    if (!window.confirm(t('liveDraw.confirmReset', { prize: premio.nombre }))) return;
    setResettingId(premio._id);
    try {
      await sorteoEnVivoService.resetPremio(premio._id);
      toast.success(t('liveDraw.resetDone'));
      await Promise.all([loadState(), loadRifas()]);
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setResettingId(null);
    }
  };

  const openStage = premioId => {
    setStagePremioId(premioId || nextPendingPremio(premios)?._id || premios[0]?._id || null);
    setStageOpen(true);
  };

  const closeStage = () => {
    setStageOpen(false);
    loadState();
  };

  const onRifaChange = e => {
    const id = e.target.value;
    setSearchParams(id ? { rifa: id } : {});
  };
  const onSorteoChange = e => {
    const id = e.target.value;
    setSearchParams(id ? { rifa: rifaId, sorteo: id } : { rifa: rifaId });
  };

  const noEligible = drawState && drawState.eligibleCount === 0;

  return (
    <div className='space-y-6'>
      <PageHeader
        title={t('liveDraw.title')}
        subtitle={t('liveDraw.subtitle')}
        actions={
          <Button onClick={() => openStage()} disabled={!drawState || premios.length === 0}>
            <Play className='h-4 w-4' />
            {t('liveDraw.openPresentation')}
          </Button>
        }
      />

      {/* Pickers */}
      <Card>
        <CardHeader>
          <CardTitle>{t('liveDraw.setup')}</CardTitle>
          <CardDescription>{t('liveDraw.setupHint')}</CardDescription>
        </CardHeader>
        <CardContent>
          {loadingRifas ? (
            <LoadingBox />
          ) : rifasError ? (
            <Notice tone='danger'>{rifasError}</Notice>
          ) : (
            <div className='grid gap-4 md:grid-cols-2 lg:grid-cols-4'>
              <label className='space-y-1.5'>
                <span className='text-sm font-medium text-foreground'>
                  {t('liveDraw.selectRaffle')}
                </span>
                <select className={SELECT_CLASS} value={rifaId} onChange={onRifaChange}>
                  <option value=''>{t('liveDraw.choose')}</option>
                  {rifas.map(r => (
                    <option key={r._id} value={r._id}>
                      {r.nombre}
                      {r.tipo === 'board' ? ` (${t('liveDraw.boardRaffle')})` : ''}
                    </option>
                  ))}
                </select>
              </label>
              <label className='space-y-1.5'>
                <span className='text-sm font-medium text-foreground'>
                  {t('liveDraw.selectDraw')}
                </span>
                <select
                  className={SELECT_CLASS}
                  value={sorteoId}
                  onChange={onSorteoChange}
                  disabled={!selectedRifa || selectedRifa.sorteos.length === 0}
                >
                  <option value=''>
                    {selectedRifa && selectedRifa.sorteos.length === 0
                      ? t('liveDraw.noDraws')
                      : t('liveDraw.choose')}
                  </option>
                  {(selectedRifa?.sorteos || []).map(s => (
                    <option key={s._id} value={s._id}>
                      {s.titulo} ({s.premiosSorteados}/{s.totalPremios})
                    </option>
                  ))}
                </select>
              </label>
              <label className='space-y-1.5'>
                <span className='text-sm font-medium text-foreground'>
                  {t('liveDraw.nameMode')}
                </span>
                <select
                  className={SELECT_CLASS}
                  value={nameMode}
                  onChange={e => setNameMode(e.target.value)}
                >
                  <option value='short'>{t('liveDraw.nameShort')}</option>
                  <option value='full'>{t('liveDraw.nameFull')}</option>
                  <option value='hidden'>{t('liveDraw.nameHidden')}</option>
                </select>
              </label>
              <label className='space-y-1.5'>
                <span className='text-sm font-medium text-foreground'>{t('liveDraw.frame')}</span>
                <select
                  className={SELECT_CLASS}
                  value={frame}
                  onChange={e => setFrame(e.target.value)}
                >
                  <option value='fill'>{t('liveDraw.frameFill')}</option>
                  <option value='vertical'>{t('liveDraw.frameVertical')}</option>
                  <option value='horizontal'>{t('liveDraw.frameHorizontal')}</option>
                </select>
              </label>
            </div>
          )}
          {!loadingRifas && !rifasError && rifas.length === 0 && (
            <p className='mt-3 text-sm text-muted-foreground'>{t('liveDraw.noRaffles')}</p>
          )}
        </CardContent>
      </Card>

      {/* Draw state */}
      {sorteoId &&
        (loadingState && !drawState ? (
          <LoadingBox />
        ) : stateError ? (
          <Notice tone='danger'>{stateError}</Notice>
        ) : drawState ? (
          <>
            <div className='grid gap-4 sm:grid-cols-3'>
              <StatCard
                icon={Ticket}
                label={t('liveDraw.eligibleTickets')}
                value={drawState.eligibleCount}
              />
              <StatCard
                icon={Trophy}
                label={t('liveDraw.prizesDrawn')}
                value={`${drawnCount} / ${premios.length}`}
              />
              <StatCard
                icon={ShieldCheck}
                label={t('liveDraw.fairnessTitle')}
                value={t('liveDraw.fairnessValue')}
                small
              />
            </div>

            {noEligible && <Notice tone='warning'>{t('liveDraw.errors.noEligible')}</Notice>}

            <Card>
              <CardHeader>
                <CardTitle>{t('liveDraw.prizes')}</CardTitle>
                <CardDescription>{t('liveDraw.drawOrderHint')}</CardDescription>
              </CardHeader>
              <CardContent>
                {premios.length === 0 ? (
                  <p className='text-sm text-muted-foreground'>{t('liveDraw.noPrizes')}</p>
                ) : (
                  <ol className='m-0 space-y-2 p-0' style={{ listStyle: 'none' }}>
                    {premios.map((p, idx) => {
                      const drawn = hasWinner(p);
                      return (
                        <li
                          key={p._id}
                          className='flex flex-col gap-3 rounded-lg border border-border p-3 sm:flex-row sm:items-center'
                        >
                          <div className='flex min-w-0 flex-1 items-center gap-3'>
                            <span className='flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-bold text-primary'>
                              {p.puesto}º
                            </span>
                            <div className='min-w-0'>
                              <p className='m-0 truncate font-medium text-foreground'>{p.nombre}</p>
                              <p className='m-0 truncate text-xs text-muted-foreground'>
                                {t('liveDraw.drawStep', { n: idx + 1 })}
                                {p.descripcion ? ` · ${p.descripcion}` : ''}
                              </p>
                            </div>
                          </div>
                          <div className='flex flex-wrap items-center gap-2'>
                            {drawn ? (
                              <>
                                <Badge variant='success'>
                                  {t('liveDraw.winner')}: {p.nroGanador}
                                </Badge>
                                {p.ganadorNombre && (
                                  <span className='text-sm text-foreground'>
                                    {formatWinnerName(p.ganadorNombre, 'full')}
                                  </span>
                                )}
                                {p.sorteadoAt && (
                                  <span className='text-xs text-muted-foreground'>
                                    {new Date(p.sorteadoAt).toLocaleString()}
                                  </span>
                                )}
                                <Button
                                  size='sm'
                                  variant='outline'
                                  onClick={() => openStage(p._id)}
                                >
                                  <Play className='h-4 w-4' />
                                  {t('liveDraw.show')}
                                </Button>
                                <Button
                                  size='sm'
                                  variant='ghost'
                                  onClick={() => handleReset(p)}
                                  disabled={resettingId === p._id}
                                >
                                  <RotateCcw className='h-4 w-4' />
                                  {t('liveDraw.reset')}
                                </Button>
                              </>
                            ) : (
                              <>
                                <Badge variant='secondary'>{t('liveDraw.pending')}</Badge>
                                <Button
                                  size='sm'
                                  onClick={() => openStage(p._id)}
                                  disabled={noEligible}
                                >
                                  <Trophy className='h-4 w-4' />
                                  {t('liveDraw.drawAction')}
                                </Button>
                              </>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ol>
                )}
              </CardContent>
            </Card>
          </>
        ) : null)}

      {stageOpen && drawState && (
        <DrawStage
          rifa={drawState.rifa}
          sorteo={drawState.sorteo}
          premios={drawState.premios}
          eligibleNumbers={drawState.eligibleNumbers}
          initialPremioId={stagePremioId}
          nameMode={nameMode}
          onNameModeChange={setNameMode}
          frame={frame}
          onFrameChange={setFrame}
          onDraw={handleDraw}
          onClose={closeStage}
        />
      )}
    </div>
  );
}

function Notice({ tone = 'warning', children }) {
  const cls =
    tone === 'danger'
      ? 'border-destructive/40 bg-destructive/10 text-destructive'
      : 'border-amber-400/50 bg-amber-50 text-amber-900 dark:bg-amber-500/10 dark:text-amber-200';
  return <div className={`rounded-lg border px-4 py-3 text-sm ${cls}`}>{children}</div>;
}

function StatCard({ icon: Icon, label, value, small = false }) {
  return (
    <Card>
      <CardContent className='flex items-center gap-3 p-4'>
        <span className='flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary'>
          <Icon className='h-5 w-5' />
        </span>
        <div className='min-w-0'>
          <p className='m-0 text-xs font-medium uppercase tracking-wide text-muted-foreground'>
            {label}
          </p>
          <p className={`m-0 font-semibold text-foreground ${small ? 'text-sm' : 'text-2xl'}`}>
            {value}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
